import React, { useEffect, useState, ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Box,
  IconButton,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  CardHeader,
  Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ImageUpload from "../../components/ImageUpload";

import FormattableTextField from '../../components/FormattableTextField';
import YearPicker from "../../components/YearPicker";

import { handleApiError } from "../../utils/apiErrorHandler";
import { paymentService } from "../../services/paymentService";

const baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// Types
type JudgeRule = {
  minAthletes: number;
  maxAthletes: number;
  requiredJudges: number;
  type: string;
};

type OfficialRule = {
  minAthletes: number;
  maxAthletes: number;
  maxOfficialsAllowed: number;
  type: string;
};

type CompetitionGroup = {
  _id: string;
  name: string;
  type: string;
  gender: string;
  ageGroup?: {
    minYear?: number;
    maxYear?: number;
  };
  description?: string;
};

type Competition = {
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  registrationOpenDate: string;
  organizer: string;
  contactPerson: string;
  sporttechBaseUrl: string;
  fees: { elite: string; basic: string };
  penaltyFees: { elite: string; basic: string };
  penaltyFeeCaps: { elite: string; basic: string };
  judgeRules: JudgeRule[];
  minJudgeLicenseBasis: string;
  minJudgeLicenseElite: string;
  officialRules: OfficialRule[];
  competitionGroups: CompetitionGroup[];
  registrationLimits: { elite: string; basic: string };
  tShirt: { available: boolean; individualizable: boolean; priceStandard: number; priceIndividualized: number };
  catering: {
    dinnerFriday: { available: boolean; price: number };
    dinnerFridayVeg: { available: boolean; price: number };
    breakfastSaturday: { available: boolean; price: number };
    breakfastSunday: { available: boolean; price: number };
  };
  overnight: {
    fridayToSaturday: { available: boolean; price: number };
    saturdayToSunday: { available: boolean; price: number };
  };
  // New fields added to the model
  paymentInstructions: string;
  additionalNotes: string;
  hasLogo: boolean;
  hasIcon: boolean;
  onlyBasisRegistrationAvailable: boolean;
  enableOfficial: boolean;
};



const CompetitionFormPage: React.FC = () => {
  // State and hooks
  const { id } = useParams();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<{ _id: string; name: string }[]>([]);
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  
  const [competition, setCompetition] = useState<Competition>({
    name: "",
    location: "",
    startDate: "",
    endDate: "",
    registrationDeadline: "",
    registrationOpenDate: "",
    organizer: "",
    contactPerson: "",
    sporttechBaseUrl: "",
    fees: { elite: "", basic: "" },
    penaltyFees: { elite: "", basic: "" },
    penaltyFeeCaps: { elite: "", basic: "" },
    judgeRules: [],
    minJudgeLicenseBasis: "",
    minJudgeLicenseElite: "",
    officialRules: [],
    competitionGroups: [],
    registrationLimits: { elite: "", basic: "" },
    tShirt: { available: false, individualizable: false, priceStandard: 0, priceIndividualized: 0 },
    catering: {
      dinnerFriday: { available: false, price: 0 },
      dinnerFridayVeg: { available: false, price: 0 },
      breakfastSaturday: { available: false, price: 0 },
      breakfastSunday: { available: false, price: 0 },
    },
    overnight: {
      fridayToSaturday: { available: false, price: 0 },
      saturdayToSunday: { available: false, price: 0 },
    },
    // Initialize new fields
    paymentInstructions: "",
    additionalNotes: "",
    hasLogo: false,
    hasIcon: false,
    onlyBasisRegistrationAvailable: false,
    enableOfficial: true
  });
  const [users, setUsers] = useState<{ _id: string; name: string }[]>([]);

  //fuer tooltip popup
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // 🔹 Vereine aus der API abrufen
  useEffect(() => {
    axios.get("/api/clubs")
      .then(response => setClubs(response.data))
      .catch(error => handleApiError(error, "Fehler beim Laden der Vereine."));
  }, []);

  // 🔹 Wettkampf-Daten laden (falls Bearbeitung)
  useEffect(() => {
    if (id) {
      axios.get(`/api/competitions/${id}`)
        .then(response => {
          const data = response.data;
        
          // 🔹 Datum umwandeln: ISO → "yyyy-MM-dd"
          // 🔹 Falls API `null` oder `undefined` zurückgibt, setzen wir leere Arrays
          const formattedCompetition = {
            ...data,
            startDate: data.startDate.split("T")[0], // "2024-06-16T00:00:00.000Z" → "2024-06-16"
            endDate: data.endDate.split("T")[0],
            registrationDeadline: data.registrationDeadline.split("T")[0],
            registrationOpenDate: data.registrationOpenDate.split("T")[0],
            judgeRules: Array.isArray(data.judgeRules) ? data.judgeRules : [],
            minJudgeLicenseBasis: data.minJudgeLicenseBasis || "",
            minJudgeLicenseElite: data.minJudgeLicenseElite || "",
            officialRules: Array.isArray(data.officialRules) ? data.officialRules : [],
            organizer: data.organizer?._id || data.organizer || "", // Sicherstellen, dass organizer niemals null ist
            penaltyFeeCaps: {
              elite: data.penaltyFeeCaps?.elite ?? "",
              basic: data.penaltyFeeCaps?.basic ?? "",
            },
            competitionGroups: Array.isArray(data.competitionGroups)
              ? data.competitionGroups.map((group: CompetitionGroup) => ({
                  ...group,
                  ageGroup: group.ageGroup || { minYear: undefined, maxYear: undefined },
                }))
              : [],
            // Set default values for new fields if they're missing in the response
            paymentInstructions: data.paymentInstructions || "",
            additionalNotes: data.additionalNotes || "",
            hasLogo: data.hasLogo || false,
            hasIcon: data.hasIcon || false,
            onlyBasisRegistrationAvailable: data.onlyBasisRegistrationAvailable || false,
            enableOfficial: data.enableOfficial ?? true
          };
          
          setCompetition(formattedCompetition);
        })
        .catch(error => handleApiError(error, "Fehler beim Laden des Wettkampfs."));
    }
  }, [id]);

  // Check for logo and icon status
  useEffect(() => {
    if (id) {
      axios.get(`/api/competitions/${id}/image-status`)
        .then(response => {
          setCompetition(prev => ({
            ...prev,
            hasLogo: response.data.hasLogo,
            hasIcon: response.data.hasIcon
          }));
        })
        .catch(error => handleApiError(error, "Fehler beim Abrufen des Bild-Status."));
    }
  }, [id]);

  useEffect(() => {
    axios.get("/api/users")
      .then(response => setUsers(response.data))
      .catch(error => handleApiError(error, "Fehler beim Laden der Benutzer."));
  }, []);

  // Neuen State hinzufügen
  const [pendingContactPersonId, setPendingContactPersonId] = useState<string>("");
  const [currentUserRole, setCurrentUserRole] = useState<string>("");

  // Auto-Fill für Organisatoren
  useEffect(() => {
    if (!id) {
      const loadAndAutoFill = async () => {
        try {
          const response = await axios.get("/api/auth/check-auth");
          const userData = response.data;
          
          setCurrentUserRole(userData.role || "");
          
          if (userData.role === 'organizer') {
            const clubId = userData.club || "";
            const userId = userData.userId || userData._id || "";
            
            // Club sofort setzen
            setCompetition(prev => ({
              ...prev,
              organizer: clubId
            }));
            
            // User-ID für später speichern
            setPendingContactPersonId(userId);
          }
        } catch (error) {
          handleApiError(error, "Fehler beim Laden der Benutzerdaten.")          
        }
      };

      loadAndAutoFill();
    }
  }, [id]);

  // User setzen, sobald users geladen sind
  useEffect(() => {
    if (!id && pendingContactPersonId && users.length > 0) {
            
      const userExists = users.find(u => u._id === pendingContactPersonId);
      
      if (userExists) {
        setCompetition(prev => ({
          ...prev,
          contactPerson: pendingContactPersonId
        }));
        
        setMessage("Als Organisator wurden Veranstalter und Ansprechpartner automatisch befüllt.");
        
        // State aufräumen
        setPendingContactPersonId("");
      } else {
        console.warn("⚠️ User not found in list:", {
          searchingFor: pendingContactPersonId,
          availableUsers: users.map(u => u._id)
        });
      }
    }
  }, [id, pendingContactPersonId, users]);
  
  const now = new Date();
  const competitionStarted = now >= new Date(competition.registrationOpenDate);
  
  const isFieldEditable = (fieldName: string) => {
    // Felder, die immer bearbeitet werden können
    const alwaysEditableFields = ['sporttechBaseUrl', 'registrationOpenDate', 'registrationDeadline', 'registrationLimits.basic','registrationLimits.elite','paymentInstructions', 'additionalNotes'];
    
    // Wenn der Wettkampf noch nicht begonnen hat, ist alles bearbeitbar
    if (!competitionStarted) {
      return true;
    }
    
    // Sonst nur die immer bearbeitbaren Felder
    return alwaysEditableFields.includes(fieldName);
  };

  // 🔹 Eingaben verarbeiten
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof Competition
  ) => {
    setCompetition({ ...competition, [field]: e.target.value });
  };

  // 🔹 Gebühren bearbeiten
  const handleFeeChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof Competition["fees"],
    type: "fees" | "penaltyFees" | "penaltyFeeCaps"
  ) => {
    setCompetition({ ...competition, [type]: { ...competition[type], [field]: e.target.value } });
  };

  // 🔹 Gebühren bearbeiten
  const handleRegLimitChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof Competition["registrationLimits"]
  ) => {
    setCompetition({ ...competition, registrationLimits: { ...competition.registrationLimits, [field]: e.target.value } });
  };

  //Mindestlizenzen bearbeiten
  const handleLicenseChange = (event: SelectChangeEvent, field: "minJudgeLicenseBasis" | "minJudgeLicenseElite") => {
    setCompetition({ ...competition, [field]: event.target.value });
  };

  const handleOrganizerChange = (event: SelectChangeEvent) => {
    setCompetition({ ...competition, organizer: event.target.value });
  };

  const handleContactPersonChange = (event: SelectChangeEvent) => {
    setCompetition({ ...competition, contactPerson: event.target.value });
  };

  // 🔹 Kampfrichter-Regeln bearbeiten
  const updateJudgeRule = (index: number, field: keyof JudgeRule, value: string | number) => {
    const updatedRules: JudgeRule[] = [...competition.judgeRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setCompetition({ ...competition, judgeRules: updatedRules });
  };

  const saveJudgeRules = async () => {
    try {
      await axios.put(`/api/competitions/${id}/judgeRules`, { judgeRules: competition.judgeRules });
      setMessage("Kampfrichter-Regeln gespeichert!");
    } catch (error) {
      //handleApiError(error, "Fehler beim Speichern der Kampfrichter-Regeln.");
      setError("Fehler beim Speichern der Kampfrichter-Regeln.");
    }
  };

  const addJudgeRule = () => {
    setCompetition({
      ...competition,
      judgeRules: [
        ...competition.judgeRules,
        { minAthletes: 0, maxAthletes: 0, requiredJudges: 0, type: "" }
      ]
    });
  };

  const removeJudgeRule = (index: number) => {
    setCompetition({
      ...competition,
      judgeRules: competition.judgeRules.filter((_, i) => i !== index),
    });
  };

  // 🔹 Offiziellen-Regeln bearbeiten
  const updateOfficialRule = (index: number, field: keyof OfficialRule, value: string | number) => {
    const updatedRules: OfficialRule[] = [...competition.officialRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setCompetition({ ...competition, officialRules: updatedRules });
  };

  const saveOfficialRules = async () => {
    try {
      await axios.put(`/api/competitions/${id}/officialRules`, { officialRules: competition.officialRules });
      setMessage("Offiziellen-Regeln gespeichert!");
    } catch (error) {
      //handleApiError(error, "Fehler beim Speichern der Offiziellen-Regeln.");
      setError("Fehler beim Speichern der Offiziellen-Regeln.");
    }
  };
  
  const addOfficialRule = () => {
    setCompetition({
      ...competition,
      officialRules: [
        ...competition.officialRules,
        { minAthletes: 0, maxAthletes: 0, maxOfficialsAllowed: 0, type: "" }
      ]
    });
  };

  const removeOfficialRule = (index: number) => {
    setCompetition({
      ...competition,
      officialRules: competition.officialRules.filter((_, i) => i !== index),
    });
  };

  // 🔹 Wettkampf speichern
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formattedCompetition = {
        ...competition,
        startDate: new Date(competition.startDate).toISOString(),
        endDate: new Date(competition.endDate).toISOString(),
        registrationDeadline: new Date(competition.registrationDeadline).toISOString(),
        registrationOpenDate: new Date(competition.registrationOpenDate).toISOString(),
      };

      if (id) {
        if (competitionStarted) {
          // Wenn der Wettkampf begonnen hat, nur erlaubte Felder aktualisieren
          const limitedUpdateData = {
            sporttechBaseUrl: formattedCompetition.sporttechBaseUrl,
            registrationLimits: formattedCompetition.registrationLimits,
            registrationDeadline: formattedCompetition.registrationDeadline,
            registrationOpenDate: formattedCompetition.registrationOpenDate,
            paymentInstructions: formattedCompetition.paymentInstructions,
            additionalNotes: formattedCompetition.additionalNotes,
          };
          await axios.put(`/api/competitions/${id}/limited-edit`, limitedUpdateData);
        } else {
          // Wenn der Wettkampf noch nicht begonnen hat, alle Felder aktualisieren
          await axios.put(`/api/competitions/${id}`, formattedCompetition);
        }
        setMessage("Wettkampf erfolgreich aktualisiert!");        
      } else {
        // Neuen Wettkampf erstellen
        await axios.post("/api/competitions", formattedCompetition);
        setMessage("Wettkampf erfolgreich erstellt!");
        navigate("/admin/competitions");
      }
    } catch (error) {
      //handleApiError(error, "Fehler beim Speichern des Wettkampfs.");      
      setError("Fehler beim Speichern des Wettkampfs.");
    }
  };

  const updateGroup = async (index: number, field: keyof CompetitionGroup, value: any) => {
    setCompetition((prev) => {
      const updatedGroups = [...prev.competitionGroups];

      if(field === "ageGroup") {
        updatedGroups[index] = { 
          ...updatedGroups[index], 
          ageGroup: { ...updatedGroups[index].ageGroup, ...value} 
        };
      } else {
        updatedGroups[index] = { ...updatedGroups[index], [field]: value };
      }

      return { ...prev, competitionGroups: updatedGroups };
    });
    
    if (competition.competitionGroups[index]._id) {
      try {
        await axios.put(`/api/competitions/${id}/groups/${competition.competitionGroups[index]._id}`, competition.competitionGroups[index]);
      } catch (error) {
        //handleApiError(error, "Fehler beim Aktualisieren der Wettkampfklasse.");
        setError("Fehler beim Aktualisieren der Wettkampfklasse.");
      }
    }
  };

  const addGroup = async () => {
    const newGroup = { name: "Name", type: "Elite", gender: "m", ageGroup: { minYear: null, maxYear: null }, description: "" };
  
    try {
      const response = await axios.put(`/api/competitions/${id}/groups`, newGroup);
      setCompetition((prev) => ({ ...prev, competitionGroups: response.data.competitionGroups }));
    } catch (error) {
      //handleApiError(error, "Fehler beim Hinzufügen der Wettkampfklasse.");
      setError("Fehler beim Hinzufügen der Wettkampfklasse.");
    }
  };

  const deleteGroup = async (groupId: string) => {
    if (!window.confirm("Wettkampfklasse wirklich löschen?")) return;
  
    try {
      await axios.delete(`/api/competitions/${id}/groups/${groupId}`);
      setCompetition((prev) => ({
        ...prev,
        competitionGroups: prev.competitionGroups.filter((g) => g._id !== groupId),
      }));
    } catch (error) {
      //handleApiError(error, "Fehler beim Löschen der Wettkampfklasse.");
      setError("Fehler beim Löschen der Wettkampfklasse.");
    }
  };

  const handleTShirtChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof typeof competition.tShirt
  ) => {
    const value = e.target.type === 'checkbox' 
      ? e.target.checked 
      : e.target.type === 'number' 
      ? Number(e.target.value) 
      : e.target.value; 
    setCompetition(prev => ({
      ...prev,
      tShirt: {
        ...prev.tShirt,
        [field]: value
      }
    }));    
  };
  
  const handleCateringChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    meal: keyof typeof competition.catering,
    field: 'available' | 'price'
  ) => {
    const value = field === 'available' 
      ? e.target.checked 
      : Number(e.target.value);
    setCompetition(prev => ({
      ...prev,
      catering: {
        ...prev.catering,
        [meal]: {
          ...prev.catering[meal],
          [field]: value
        }
      }
    }));
  };

  const handleOvernightChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    night: keyof typeof competition.overnight,
    field: 'available' | 'price'
  ) => {
    const value = field === 'available'
      ? e.target.checked
      : Number(e.target.value);
    setCompetition(prev => ({
      ...prev,
      overnight: {
        ...prev.overnight,
        [night]: {
          ...prev.overnight[night],
          [field]: value
        }
      }
    }));
  };

  // New handlers for logo and icon
  const handleLogoSuccess = () => {
    setMessage("Logo wurde erfolgreich hochgeladen!");
    setCompetition(prev => ({...prev, hasLogo: true}));
  };

  const handleIconSuccess = () => {
    setMessage("Icon wurde erfolgreich hochgeladen!");
    setCompetition(prev => ({...prev, hasIcon: true}));
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`/api/images/Competition/${id}/logo`);
      setMessage("Logo wurde erfolgreich gelöscht!");
      setCompetition(prev => ({...prev, hasLogo: false}));
    } catch (error) {
      //handleApiError(error, "Fehler beim Löschen des Logos.");
      setError("Fehler beim Löschen des Logos");
    }
  };

  const handleIconDelete = async () => {
    try {
      await axios.delete(`/api/images/Competition/${id}/icon`);
      setMessage("Icon wurde erfolgreich gelöscht!");
      setCompetition(prev => ({...prev, hasIcon: false}));
    } catch (error) {
      //handleApiError(error, "Fehler beim Löschen des Icons.");
      setError("Fehler beim Löschen des Icons");
    }
  };

  const isFieldDisabledForOrganizer = (fieldName: string): boolean => {
    // Für Organisatoren: Veranstalter und Ansprechpartner sperren
    if (currentUserRole === 'organizer' && !id) {
      return ['organizer', 'contactPerson'].includes(fieldName);
    }
    return false;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          {id ? "Wettkampf bearbeiten" : "Neuen Wettkampf erstellen"}
        </Typography>
        
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={competition.name}
                onChange={(e) => handleChange(e, "name")}
                required
                margin="normal"
                disabled={!isFieldEditable("name")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ort"
                value={competition.location}
                onChange={(e) => handleChange(e, "location")}
                required
                margin="normal"
                disabled={!isFieldEditable("location")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Meldestart"
                value={competition.registrationOpenDate}
                onChange={(e) => handleChange(e, "registrationOpenDate")}
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled={!isFieldEditable("registrationOpenDate")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Meldeschluss"
                value={competition.registrationDeadline}
                onChange={(e) => handleChange(e, "registrationDeadline")}
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled={!isFieldEditable("registrationDeadline")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Startdatum"
                value={competition.startDate}
                onChange={(e) => handleChange(e, "startDate")}
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled={!isFieldEditable("startDate")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Enddatum"
                value={competition.endDate}
                onChange={(e) => handleChange(e, "endDate")}
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled={!isFieldEditable("endDate")}
              />
            </Grid>

            {/* Organizer */}
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Veranstalter</InputLabel>
                <Select
                  value={competition.organizer}
                  onChange={handleOrganizerChange}
                  required
                  label="Veranstalter"
                  disabled={!isFieldEditable("organizer") || isFieldDisabledForOrganizer("organizer")}
                >
                  <MenuItem value="">
                    <em>-- Verein auswählen --</em>
                  </MenuItem>
                  {clubs.map((club) => (
                    <MenuItem key={club._id} value={club._id}>
                      {club.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Contact Person */}
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Ansprechpartner</InputLabel>
                <Select
                  value={competition.contactPerson}
                  onChange={handleContactPersonChange}
                  required
                  label="Ansprechpartner"
                  disabled={!isFieldEditable("contactPerson") || isFieldDisabledForOrganizer("contactPerson")}
                >
                  <MenuItem value="">
                    <em>-- Ansprechpartner auswählen --</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sporttech Base URL */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SportTech Base URL"
                value={competition.sporttechBaseUrl}
                onChange={(e) => handleChange(e, "sporttechBaseUrl")}                
                margin="normal"
                disabled={!isFieldEditable("sporttechBaseUrl")}
              />
            </Grid>

            {/* Einstellungen */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Einstellungen</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.onlyBasisRegistrationAvailable}
                        onChange={(e) => setCompetition({ ...competition, onlyBasisRegistrationAvailable: e.target.checked })}
                      />
                    }
                    label="Nur Basis-Meldung verfügbar (keine Elite-Option)"
                    disabled={!isFieldEditable("onlyBasisRegistrationAvailable")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.enableOfficial}
                        onChange={(e) => setCompetition({ ...competition, enableOfficial: e.target.checked })}
                      />
                    }
                    label="Offizielle-Meldung aktiviert"
                    disabled={!isFieldEditable("enableOfficial")}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Fees */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Meldegebühr Elite (€)"
                value={competition.fees.elite}
                onChange={(e) => handleFeeChange(e, "elite", "fees")}
                required
                margin="normal"
                disabled={!isFieldEditable("fees.elite")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Meldegebühr Basis (€)"
                value={competition.fees.basic}
                onChange={(e) => handleFeeChange(e, "basic", "fees")}
                required
                margin="normal"
                disabled={!isFieldEditable("fees.basic")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Strafgebühr Elite (€)"
                value={competition.penaltyFees.elite}
                onChange={(e) => handleFeeChange(e, "elite", "penaltyFees")}
                required
                margin="normal"
                disabled={!isFieldEditable("penaltyFees.elite")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Strafgebühr Basis (€)"
                value={competition.penaltyFees.basic}
                onChange={(e) => handleFeeChange(e, "basic", "penaltyFees")}
                required
                margin="normal"
                disabled={!isFieldEditable("penaltyFees.basic")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Obergrenze Strafgebühr Elite (€)"
                helperText="Leer lassen für keine Begrenzung"
                value={competition.penaltyFeeCaps.elite}
                onChange={(e) => handleFeeChange(e, "elite", "penaltyFeeCaps")}
                margin="normal"
                disabled={!isFieldEditable("penaltyFeeCaps.elite")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Obergrenze Strafgebühr Basis (€)"
                helperText="Leer lassen für keine Begrenzung"
                value={competition.penaltyFeeCaps.basic}
                onChange={(e) => handleFeeChange(e, "basic", "penaltyFeeCaps")}
                margin="normal"
                disabled={!isFieldEditable("penaltyFeeCaps.basic")}
              />
            </Grid>

            {/* Registration Limits */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max. Anzahl Teilnehmer Elite"
                value={competition.registrationLimits.elite}
                onChange={(e) => handleRegLimitChange(e, "elite")}
                margin="normal"
                disabled={!isFieldEditable("registrationLimits.elite")}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Max. Anzahl Teilnehmer Basis"
                value={competition.registrationLimits.basic}
                onChange={(e) => handleRegLimitChange(e, "basic")}
                margin="normal"
                disabled={!isFieldEditable("registrationLimits.basic")}
              />
            </Grid>

            {/* Zeilenumbruch einfügen */}
            <Grid item xs={12} md={3}>
              <Box></Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box></Box>
            </Grid>

            {/* Felder für Min. Kampfrichterlizenzen */}
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Min. Kampfrichterlizenz Elite"
                value={competition.minJudgeLicenseElite}
                onChange={(e) => handleLicenseChange(e as SelectChangeEvent, "minJudgeLicenseElite")}
                margin="normal"
                required
                disabled={!isFieldEditable("minJudgeLicenseElite")}
              >
                <MenuItem value="keine Vorgabe">
                  <em>Keine Vorgabe</em>
                </MenuItem>
                <MenuItem value="D-Lizenz">D-Lizenz</MenuItem>
                <MenuItem value="C-Lizenz">C-Lizenz</MenuItem>
                <MenuItem value="B-Lizenz">B-Lizenz</MenuItem>
                <MenuItem value="A-Lizenz">A-Lizenz</MenuItem>
                <MenuItem value="FIG Lizenz">FIG Lizenz</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Min. Kampfrichterlizenz Basis"
                value={competition.minJudgeLicenseBasis}
                onChange={(e) => handleLicenseChange(e as SelectChangeEvent, "minJudgeLicenseBasis")}
                margin="normal"
                required
                disabled={!isFieldEditable("minJudgeLicenseBasis")}
              >
                <MenuItem value="keine Vorgabe">
                  <em>Keine Vorgabe</em>
                </MenuItem>
                <MenuItem value="D-Lizenz">D-Lizenz</MenuItem>
                <MenuItem value="C-Lizenz">C-Lizenz</MenuItem>
                <MenuItem value="B-Lizenz">B-Lizenz</MenuItem>
                <MenuItem value="A-Lizenz">A-Lizenz</MenuItem>
                <MenuItem value="FIG Lizenz">FIG Lizenz</MenuItem>
              </TextField>
            </Grid>


            {/* Additional Information */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>Zusätzliche Informationen</Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormattableTextField
                      fullWidth
                      label="Zahlungshinweise"
                      multiline
                      rows={10}
                      value={competition.paymentInstructions}
                      onChange={(e) => handleChange(e, "paymentInstructions")}
                      placeholder="IBAN, BIC, Kontoinhaber, etc."
                      margin="normal"
                      fieldName="paymentInstructions"
                      disabled={!isFieldEditable("paymentInstructions")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormattableTextField
                      fullWidth
                      label="Zusätzliche Hinweise"
                      multiline
                      rows={12}
                      value={competition.additionalNotes}
                      onChange={(e) => handleChange(e, "additionalNotes")}
                      placeholder="Zusätzliche Informationen zum Wettkampf"
                      margin="normal"
                      fieldName="additionalNotes"
                      disabled={!isFieldEditable("additionalNotes")}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* T-Shirt Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>T-Shirt</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.tShirt.available}
                        onChange={(e) => handleTShirtChange(e, "available")}
                      />
                    }
                    label="T-Shirts verfügbar"
                    disabled={!isFieldEditable("tShirt.available")}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.tShirt.individualizable}
                        onChange={(e) => handleTShirtChange(e, "individualizable")}
                      />
                    }
                    label="Individualisierbar"
                    disabled={!isFieldEditable("tShirt.individualizable")}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Preis Standard (€)"
                  value={competition.tShirt.priceStandard}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTShirtChange(e, "priceStandard")}
                  margin="normal"
                  disabled={!isFieldEditable("tShirt.priceStandard")}
                />
                </Grid>
                <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    type="number"
                    label="Preis individualisiert (€)"
                    value={competition.tShirt.priceIndividualized}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTShirtChange(e, "priceIndividualized")}
                    margin="normal"
                    disabled={!isFieldEditable("tShirt.priceIndividualized")}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Catering Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Verpflegung</Typography>
              <Grid container spacing={2}>
                {/* Friday Dinner */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.catering.dinnerFriday.available}
                        onChange={(e) => handleCateringChange(e, "dinnerFriday", "available")}
                      />
                    }
                    label="Abendessen Freitag verfügbar"
                    disabled={!isFieldEditable("catering.dinnerFriday.available")}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis (€)"
                    value={competition.catering.dinnerFriday.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCateringChange(e, "dinnerFriday", "price")}
                    margin="normal"
                    disabled={!isFieldEditable("catering.dinnerFriday.price")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.catering.dinnerFridayVeg.available}
                        onChange={(e) => handleCateringChange(e, "dinnerFridayVeg", "available")}
                      />
                    }
                    label="Abendessen Freitag (vegetarisch) verfügbar"
                    disabled={!isFieldEditable("catering.dinnerFridayVeg.available")}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis (€)"
                    value={competition.catering.dinnerFridayVeg.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCateringChange(e, "dinnerFridayVeg", "price")}
                    margin="normal"
                    disabled={!isFieldEditable("catering.dinnerFridayVeg.price")}
                  />
                </Grid>
                
                {/* Breakfast Options */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.catering.breakfastSaturday.available}
                        onChange={(e) => handleCateringChange(e, "breakfastSaturday", "available")}
                      />
                    }
                    label="Frühstück Samstag verfügbar"
                    disabled={!isFieldEditable("catering.breakfastSaturday.available")}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis (€)"
                    value={competition.catering.breakfastSaturday.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCateringChange(e, "breakfastSaturday", "price")}
                    margin="normal"
                    disabled={!isFieldEditable("catering.breakfastSaturday.price")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.catering.breakfastSunday.available}
                        onChange={(e) => handleCateringChange(e, "breakfastSunday", "available")}
                      />
                    }
                    label="Frühstück Sonntag verfügbar"
                    disabled={!isFieldEditable("catering.breakfastSunday.available")}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis (€)"
                    value={competition.catering.breakfastSunday.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCateringChange(e, "breakfastSunday", "price")}
                    margin="normal"
                    disabled={!isFieldEditable("catering.breakfastSunday.price")}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Overnight Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Übernachtung</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.overnight.fridayToSaturday.available}
                        onChange={(e) => handleOvernightChange(e, "fridayToSaturday", "available")}
                      />
                    }
                    label="Übernachtung Freitag auf Samstag verfügbar"
                    disabled={!isFieldEditable("overnight.fridayToSaturday.available")}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis pro Person (€)"
                    value={competition.overnight.fridayToSaturday.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOvernightChange(e, "fridayToSaturday", "price")}
                    margin="normal"
                    disabled={!isFieldEditable("overnight.fridayToSaturday.price")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={competition.overnight.saturdayToSunday.available}
                        onChange={(e) => handleOvernightChange(e, "saturdayToSunday", "available")}
                      />
                    }
                    label="Übernachtung Samstag auf Sonntag verfügbar"
                    disabled={!isFieldEditable("overnight.saturdayToSunday.available")}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Preis pro Person (€)"
                    value={competition.overnight.saturdayToSunday.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOvernightChange(e, "saturdayToSunday", "price")}
                    margin="normal"
                    disabled={!isFieldEditable("overnight.saturdayToSunday.price")}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SaveIcon />}
              >
                Wettkampf speichern
              </Button>
              <Divider sx={{ my: 3 }} />
            </Grid>

            {/* Image upload section - only visible when editing an existing competition */}
            {id && (
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
                  <Typography variant="h6" gutterBottom>Bilder</Typography>
                  
                  <Grid container spacing={3}>
                    {/* Logo Upload */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={2}>
                        <CardHeader 
                          title="Wettkampf-Logo"
                          sx={{ backgroundColor: 'primary.light', color: 'white' }}
                        />
                        <CardContent>
                          {competition.hasLogo ? (
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{ mb: 2 }}>
                                <img 
                                  src={`${baseURL}/api/images/Competition/${id}/thumbnail/logo`} 
                                  alt="Wettkampf-Logo"
                                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                                  onError={(e) => {
                                    // In case the thumbnail is not available
                                    const target = e.target as HTMLImageElement;
                                    target.src = `${baseURL}/api/images/Competition/${id}/logo`;
                                  }}
                                />
                              </Box>
                              <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleLogoDelete}
                              >
                                Logo löschen
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" gutterBottom>
                                Kein Logo vorhanden. Lade bitte ein Logo hoch (JPG oder PNG, max. 5MB).
                              </Typography>
                              <ImageUpload
                                entityId={id}
                                entityType="Competition"
                                onSuccess={handleLogoSuccess}
                                label="Wettkampf-Logo"
                                imageType="logo"
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* Icon Upload */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={2}>
                        <CardHeader 
                          title="Wettkampf-Icon"
                          sx={{ backgroundColor: 'primary.light', color: 'white' }}
                        />
                        <CardContent>
                          {competition.hasIcon ? (
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{ mb: 2 }}>
                                <img 
                                  src={`${baseURL}/api/images/Competition/${id}/thumbnail/icon`} 
                                  alt="Wettkampf-Icon"
                                  style={{ maxWidth: '100px', maxHeight: '100px' }}
                                  onError={(e) => {
                                    // In case the thumbnail is not available
                                    const target = e.target as HTMLImageElement;
                                    target.src = `${baseURL}/api/images/Competition/${id}/icon`;
                                  }}
                                />
                              </Box>
                              <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleIconDelete}
                              >
                                Icon löschen
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" gutterBottom>
                                Kein Icon vorhanden. Lade bitte ein Icon hoch (JPG oder PNG, max. 5MB).
                              </Typography>
                              <ImageUpload
                                entityId={id}
                                entityType="Competition"
                                onSuccess={handleIconSuccess}
                                label="Wettkampf-Icon"
                                imageType="icon"
                              />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Judge Rules */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Kampfrichter-Regeln
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Min. Athleten</TableCell>
                      <TableCell>Max. Athleten</TableCell>
                      <TableCell>Erforderliche Kampfrichter</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competition.judgeRules.map((rule, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rule.minAthletes}
                            onChange={(e) => updateJudgeRule(index, "minAthletes", Number(e.target.value))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rule.maxAthletes}
                            onChange={(e) => updateJudgeRule(index, "maxAthletes", Number(e.target.value))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rule.requiredJudges}
                            onChange={(e) => updateJudgeRule(index, "requiredJudges", Number(e.target.value))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={rule.type}
                            onChange={(e) => updateJudgeRule(index, "type", e.target.value)}
                            placeholder="Elite/Basis"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => removeJudgeRule(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addJudgeRule}
                >
                  Kampfrichter-Regel hinzufügen
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveJudgeRules}
                >
                  Speichern
                </Button>
              </Box>
              <Divider sx={{ my: 3 }} />
            </Grid>

            {/* Official Rules */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Offiziellen-Regeln
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Min. Athleten</TableCell>
                      <TableCell>Max. Athleten</TableCell>
                      <TableCell>Max. Offizielle</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competition.officialRules.map((rule, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rule.minAthletes}
                            onChange={(e) => updateOfficialRule(index, "minAthletes", Number(e.target.value))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rule.maxAthletes}
                            onChange={(e) => updateOfficialRule(index, "maxAthletes", Number(e.target.value))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={rule.maxOfficialsAllowed}
                            onChange={(e) => updateOfficialRule(index, "maxOfficialsAllowed", Number(e.target.value))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={rule.type}
                            onChange={(e) => updateOfficialRule(index, "type", e.target.value)}
                            placeholder="Elite/Basis"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => removeOfficialRule(index)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addOfficialRule}
                >
                  Offiziellen-Regel hinzufügen
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveOfficialRules}
                >
                  Speichern
                </Button>
              </Box>
              <Divider sx={{ my: 3 }} />
            </Grid>

            {/* Competition Groups */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Wettkampfklassen
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Geschlecht</TableCell>
                      <TableCell>Älterer Jahrgang</TableCell>
                      <TableCell>Jüngerer Jahrgang</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competition.competitionGroups.map((group, index) => (
                      <TableRow key={group._id || index}>
                        <TableCell>
                          <TextField
                            value={group.name}
                            onChange={(e) => updateGroup(index, "name", e.target.value)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={group.type}
                            onChange={(e) => updateGroup(index, "type", e.target.value)}
                            size="small"
                          >
                            <MenuItem value="Elite">Elite</MenuItem>
                            <MenuItem value="Basis">Basis</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={group.gender}
                            onChange={(e) => updateGroup(index, "gender", e.target.value)}
                            size="small"
                          >
                            <MenuItem value="m">Männlich</MenuItem>
                            <MenuItem value="w">Weiblich</MenuItem>
                            <MenuItem value="mixed">Gemischt</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <YearPicker
                            value={group.ageGroup?.minYear || null}
                            onChange={(year) => updateGroup(index, "ageGroup", { minYear: year })}
                            fullWidth={true}
                            label="Älterer Jahrgang"                    
                            variant="standard"
                            hideLabel={true}
                            disableValidation={true}
                          />
                        </TableCell>
                        <TableCell>
                          <YearPicker
                            value={group.ageGroup?.maxYear || null}
                            onChange={(year) => updateGroup(index, "ageGroup", { maxYear: year })}
                            fullWidth={true}
                            label="Jüngerer Jahrgang"                    
                            variant="standard"
                            hideLabel={true}
                            disableValidation={true}
                          />
                        </TableCell>                        
                        <TableCell>
                          <IconButton onClick={() => deleteGroup(group._id)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addGroup}
                >
                  Wettkampfklasse hinzufügen
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default CompetitionFormPage;